import { useState } from 'react';
import { Folder, MoreVertical, Check } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { WorkspaceFile } from '../../types';
import { ContextMenu, MenuItem } from '../Common';
import clsx from 'clsx';

interface FolderItemProps {
  folder: WorkspaceFile;
  selected?: boolean;
  multiSelectMode?: boolean;
  onSelect?: () => void;
  onDoubleClick?: () => void;
  onContextMenu?: () => MenuItem[];
}

export function FolderItem({
  folder,
  selected,
  multiSelectMode,
  onSelect,
  onDoubleClick,
  onContextMenu,
}: FolderItemProps) {
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);

  const lastModified = folder.lastModified
    ? formatDistanceToNow(new Date(folder.lastModified), { addSuffix: true })
    : '--';

  const handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onContextMenu) {
      setContextMenu({ x: e.clientX, y: e.clientY });
    }
  };

  return (
    <>
      <div
        className={clsx(
          'flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer border-b',
          selected && 'bg-primary-50 hover:bg-primary-100'
        )}
        onClick={multiSelectMode ? onSelect : onDoubleClick}
        onDoubleClick={onDoubleClick}
        onContextMenu={handleContextMenu}
      >
        {multiSelectMode && (
          <div
            className={clsx(
              'w-5 h-5 border-2 rounded flex items-center justify-center',
              selected
                ? 'bg-primary-600 border-primary-600'
                : 'border-gray-300'
            )}
            onClick={(e) => {
              e.stopPropagation();
              onSelect?.();
            }}
          >
            {selected && <Check className="w-3 h-3 text-white" />}
          </div>
        )}

        <Folder className="w-5 h-5 text-primary-500 flex-shrink-0" />

        <div className="flex-1 min-w-0">
          <span className="font-medium text-gray-900 truncate">
            {folder.name}
          </span>
        </div>

        <div className="text-sm text-gray-500 flex-shrink-0">--</div>

        <div className="text-sm text-gray-500 flex-shrink-0 w-28 text-right">
          {lastModified}
        </div>

        {onContextMenu && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleContextMenu(e);
            }}
            className="p-1 text-gray-400 hover:text-gray-600 rounded"
          >
            <MoreVertical className="w-4 h-4" />
          </button>
        )}
      </div>

      {contextMenu && onContextMenu && (
        <ContextMenu
          items={onContextMenu()}
          position={contextMenu}
          onClose={() => setContextMenu(null)}
        />
      )}
    </>
  );
}
